// noinspection DuplicatedCode
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";

const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
    return {};
  }
};

const getAuthHeaders = () => {
  const user = getUser();
  return {
    Authorization: "Bearer " + user.Token,
    Accept: "*/*",
  };
};

const handleResponse = (response, thunkAPI) => {
  if (response.status === 200) {
    return response.data;
  }
  return thunkAPI.rejectWithValue(response.data);
};

const handleError = (error, thunkAPI) => {
  return thunkAPI.rejectWithValue(
    error.response?.data || { message: error.message || "Network error" }
  );
};

export const getJadwalKerja = createAsyncThunk(
  "jadwalKerja/get",
  async ({ nik, start_date, end_date, page = 1, page_size = 20 }, thunkAPI) => {
    const user = getUser();
    if (!user.Token) {
      return thunkAPI.rejectWithValue({ message: "User not authenticated" });
    }

    const params = new URLSearchParams();
    if (nik) params.append("nik", nik);
    if (start_date) params.append("start_date", start_date);
    if (end_date) params.append("end_date", end_date);
    params.append("page", page);
    params.append("page_size", page_size);

    const url = `${process.env.REACT_APP_API_URL}getJadwalKerja?${params.toString()}`;

    try {
      const response = await axios.get(url, { headers: getAuthHeaders() });
      return { ...response.data, currentPage: page, pageSize: page_size };
    } catch (e) {
      return handleError(e, thunkAPI);
    }
  }
);

export const importJadwalKerja = createAsyncThunk(
  "jadwalKerja/import",
  async (excelFile, thunkAPI) => {
    const user = getUser();
    if (!user?.Token) {
      return thunkAPI.rejectWithValue({ message: "User not authenticated" });
    }
    if (!excelFile) {
      return thunkAPI.rejectWithValue({ message: "File tidak ditemukan" });
    }

    const formData = new FormData();
    formData.append("excelFile", excelFile);

    const url = process.env.REACT_APP_API_URL + "importJadwalKerja";

    try {
      const response = await axios.post(url, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      return handleResponse(response, thunkAPI);
    } catch (e) {
      let errorMessage = "Terjadi kesalahan jaringan";
      if (e.code === "ECONNABORTED") {
        errorMessage = "Timeout: Upload terlalu lama";
      } else if (e.response?.data?.message) {
        errorMessage = e.response.data.message;
      } else if (e.request) {
        errorMessage = "Tidak dapat terhubung ke server";
      }
      return thunkAPI.rejectWithValue({
        message: errorMessage,
        ...(e.response?.data || {}),
      });
    }
  }
);

export const downloadTemplateJadwalKerja = createAsyncThunk(
  "jadwalKerja/downloadTemplate",
  async (_, thunkAPI) => {
    const user = getUser();
    if (!user?.Token) {
      return thunkAPI.rejectWithValue({ message: "User not authenticated" });
    }

    const url = process.env.REACT_APP_API_URL + "downloadTemplateJadwalKerja";

    try {
      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        responseType: "blob",
      });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "template-jadwal-kerja.xlsx";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      return { message: "Template Excel berhasil didownload" };
    } catch (e) {
      return handleError(e, thunkAPI);
    }
  }
);

export const deleteJadwalKerja = createAsyncThunk(
  "jadwalKerja/delete",
  async (id, thunkAPI) => {
    const user = getUser();
    if (!user?.Token) {
      return thunkAPI.rejectWithValue({ message: "User not authenticated" });
    }

    const formData = new FormData();
    formData.append("id", id);

    const url = process.env.REACT_APP_API_URL + "deleteJadwalKerja";

    try {
      const response = await axios.post(url, formData, {
        headers: getAuthHeaders(),
      });
      return handleResponse(response, thunkAPI);
    } catch (e) {
      return handleError(e, thunkAPI);
    }
  }
);

const initialState = {
  data: [],
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
  isGetFetching: false,
  isGetError: false,
  isImportFetching: false,
  isImportSuccess: false,
  isImportError: false,
  isDownloadFetching: false,
  isDeleteFetching: false,
  message: "",
  errorMessage: "",
  importResult: null,
};

export const jadwalKerjaSlice = createSlice({
  name: "jadwalKerja",
  initialState,
  reducers: {
    clearJadwalKerjaMessage: (state) => {
      state.message = "";
      state.errorMessage = "";
      state.isImportSuccess = false;
      state.isImportError = false;
    },
    setCurrentPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getJadwalKerja.pending, (state) => {
        state.isGetFetching = true;
        state.isGetError = false;
      })
      .addCase(getJadwalKerja.fulfilled, (state, { payload }) => {
        state.isGetFetching = false;
        const d = payload.data || {};
        state.data = d.data || [];
        state.pagination = {
          currentPage: payload.currentPage || d.page || 1,
          pageSize: payload.pageSize || d.page_size || 20,
          totalCount: d.total_count || 0,
          totalPages: d.total_pages || 0,
          hasNext: d.has_next || false,
          hasPrev: d.has_prev || false,
        };
      })
      .addCase(getJadwalKerja.rejected, (state, { payload }) => {
        state.isGetFetching = false;
        state.isGetError = true;
        state.errorMessage = payload?.message || "Gagal mengambil data jadwal kerja";
        state.data = [];
      })
      .addCase(importJadwalKerja.pending, (state) => {
        state.isImportFetching = true;
        state.isImportError = false;
        state.isImportSuccess = false;
      })
      .addCase(importJadwalKerja.fulfilled, (state, { payload }) => {
        state.isImportFetching = false;
        state.isImportSuccess = true;
        state.importResult = payload.data;
        state.message = payload.message;
      })
      .addCase(importJadwalKerja.rejected, (state, { payload }) => {
        state.isImportFetching = false;
        state.isImportError = true;
        state.errorMessage = payload?.message || "Gagal import jadwal kerja";
        if (payload?.data) {
          state.importResult = payload.data;
        }
      })
      .addCase(downloadTemplateJadwalKerja.pending, (state) => {
        state.isDownloadFetching = true;
      })
      .addCase(downloadTemplateJadwalKerja.fulfilled, (state, { payload }) => {
        state.isDownloadFetching = false;
        state.message = payload.message;
      })
      .addCase(downloadTemplateJadwalKerja.rejected, (state, { payload }) => {
        state.isDownloadFetching = false;
        state.errorMessage = payload?.message || "Gagal download template";
      })
      .addCase(deleteJadwalKerja.pending, (state) => {
        state.isDeleteFetching = true;
      })
      .addCase(deleteJadwalKerja.fulfilled, (state, { payload }) => {
        state.isDeleteFetching = false;
        state.message = payload.message;
      })
      .addCase(deleteJadwalKerja.rejected, (state, { payload }) => {
        state.isDeleteFetching = false;
        state.errorMessage = payload?.message || "Gagal menghapus jadwal";
      });
  },
});

export const { clearJadwalKerjaMessage, setCurrentPage } = jadwalKerjaSlice.actions;

export const jadwalKerjaSelector = (state) => {
  const s = state.jadwalKerja || {};
  return {
    data: s.data || [],
    pagination: s.pagination || initialState.pagination,
    isGetFetching: s.isGetFetching || false,
    isImportFetching: s.isImportFetching || false,
    isDownloadFetching: s.isDownloadFetching || false,
    isDeleteFetching: s.isDeleteFetching || false,
    message: s.message || "",
    errorMessage: s.errorMessage || "",
    importResult: s.importResult || null,
  };
};

export default jadwalKerjaSlice.reducer;
